// src/pages/VoiceCallsDashboard.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
  Skeleton,
  Button,
  Alert,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Campaign as CampaignIcon,
  AudioFile as AudioFileIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const VoiceCallsDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Separate states for each data slice
  const [stats, setStats] = useState(null);
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [charts, setCharts] = useState(null);

  // Loading states
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [loadingCharts, setLoadingCharts] = useState(true);

  // Error states
  const [errorStats, setErrorStats] = useState("");
  const [errorRecent, setErrorRecent] = useState("");
  const [errorCharts, setErrorCharts] = useState("");

  const fetchAll = async () => {
    // Reset states
    setLoadingStats(true);
    setLoadingRecent(true);
    setLoadingCharts(true);
    setErrorStats("");
    setErrorRecent("");
    setErrorCharts("");

    const headers = getHeader();

    // Fetch all endpoints concurrently
    const [statsRes, recentRes, chartsRes] = await Promise.allSettled([
      axios.get(
        `${API_ENDPOINT}/voice/voice-campaigns/dashboard/stats`,
        headers
      ),
      axios.get(
        `${API_ENDPOINT}/voice/voice-campaigns/dashboard/recent`,
        headers
      ),
      axios.get(
        `${API_ENDPOINT}/voice/voice-campaigns/dashboard/charts`,
        headers
      ),
    ]);

    // Handle stats
    if (statsRes.status === "fulfilled") {
      setStats(statsRes.value.data.data);
    } else {
      setErrorStats(
        statsRes.reason?.response?.data?.message || "Failed to load stats"
      );
    }
    setLoadingStats(false);

    // Handle recent
    if (recentRes.status === "fulfilled") {
      setRecentCampaigns(recentRes.value.data.data);
    } else {
      setErrorRecent(
        recentRes.reason?.response?.data?.message ||
          "Failed to load recent campaigns"
      );
    }
    setLoadingRecent(false);

    // Handle charts
    if (chartsRes.status === "fulfilled") {
      setCharts(chartsRes.value.data.data);
    } else {
      setErrorCharts(
        chartsRes.reason?.response?.data?.message || "Failed to load charts"
      );
    }
    setLoadingCharts(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Quick actions (static)
  const quickActions = [
    {
      title: "View All Campaigns",
      description: "Manage and monitor campaigns",
      icon: <CampaignIcon />,
      color: theme.palette.primary.main,
      onClick: () => navigate("/home/campaigns"),
    },
    {
      title: "Voice Call List",
      description: "View all voice calls",
      icon: <PhoneIcon />,
      color: theme.palette.info.main,
      onClick: () => navigate("/home/voice-list"),
    },
    {
      title: "Voice Files",
      description: "Manage voice recordings",
      icon: <AudioFileIcon />,
      color: theme.palette.success.main,
      onClick: () => navigate("/home/voice-files"),
    },
    {
      title: "Upload New File",
      description: "Add voice recordings",
      icon: <AudioFileIcon />,
      color: theme.palette.warning.main,
      onClick: () => navigate("/home/upload-files"),
    },
  ];

  // Stats cards configuration (depends on stats data)
  const statsCards = stats
    ? [
        {
          title: "Total Campaigns",
          value: stats.totalCampaigns,
          icon: <CampaignIcon />,
          color: theme.palette.primary.main,
          onClick: () => navigate("/home/campaigns"),
        },
        {
          title: "Active Campaigns",
          value: stats.activeCampaigns,
          icon: <ScheduleIcon />,
          color: theme.palette.warning.main,
          onClick: () => navigate("/home/campaigns?status=active"),
        },
        {
          title: "Total Calls",
          value: stats.totalCalls.toLocaleString(),
          icon: <PhoneIcon />,
          color: theme.palette.info.main,
          onClick: () => navigate("/home/voice-list"),
        },
        {
          title: "Success Rate",
          value: `${stats.successRate}%`,
          icon: <CheckCircleIcon />,
          color: theme.palette.success.main,
          onClick: () => navigate("/home/analytics"),
        },
        {
          title: "Calls Today",
          value: stats.callsToday.toLocaleString(),
          icon: <TrendingUpIcon />,
          color: theme.palette.secondary.main,
          onClick: () => navigate("/home/voice-list?filter=today"),
        },
        {
          title: "Voice Files",
          value: stats.totalVoiceFiles,
          icon: <AudioFileIcon />,
          color: theme.palette.error.main,
          onClick: () => navigate("/home/voice-files"),
        },
      ]
    : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Voice Calls Dashboard
        </Typography>
        <IconButton onClick={fetchAll} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Real-time data from OBD API
      </Typography>

      {/* Stats Cards Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {loadingStats ? (
          // Skeleton for stats cards
          [...Array(6)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))
        ) : errorStats ? (
          // Show error for stats only
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setErrorStats("")}>
              {errorStats}
            </Alert>
          </Grid>
        ) : (
          // Render actual stats cards
          statsCards.map((card, i) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={card.title + i}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  transition: "0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: theme.shadows[8],
                  },
                }}
                onClick={card.onClick}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box>
                      <Typography
                        color="textSecondary"
                        gutterBottom
                        variant="body2"
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        variant="h5"
                        component="div"
                        fontWeight="bold"
                      >
                        {card.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <TrendingUpIcon fontSize="inherit" /> +
                        {Math.floor(Math.random() * 20) + 5}% ↑
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: alpha(card.color, 0.1),
                        borderRadius: "50%",
                        width: 48,
                        height: 48,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: card.color,
                      }}
                    >
                      {card.icon}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Weekly Call Trends
            </Typography>
            {loadingCharts ? (
              <Skeleton variant="rounded" height={300} />
            ) : errorCharts ? (
              <Alert severity="error">{errorCharts}</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={charts.callTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="calls"
                    stackId="1"
                    stroke={theme.palette.primary.main}
                    fill={alpha(theme.palette.primary.main, 0.3)}
                  />
                  <Area
                    type="monotone"
                    dataKey="successful"
                    stackId="2"
                    stroke={theme.palette.success.main}
                    fill={alpha(theme.palette.success.main, 0.3)}
                  />
                  <Area
                    type="monotone"
                    dataKey="failed"
                    stackId="3"
                    stroke={theme.palette.error.main}
                    fill={alpha(theme.palette.error.main, 0.3)}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Call Status Distribution
            </Typography>
            {loadingCharts ? (
              <Skeleton variant="rounded" height={300} />
            ) : errorCharts ? (
              <Alert severity="error">{errorCharts}</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={charts.statusDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {charts.statusDistribution.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Campaign Performance (Success Rate %)
            </Typography>
            {loadingCharts ? (
              <Skeleton variant="rounded" height={300} />
            ) : errorCharts ? (
              <Alert severity="error">{errorCharts}</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={charts.campaignPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="value" fill={theme.palette.primary.main} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Hourly Call Activity
            </Typography>
            {loadingCharts ? (
              <Skeleton variant="rounded" height={300} />
            ) : errorCharts ? (
              <Alert severity="error">{errorCharts}</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={charts.hourlyActivity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="calls"
                    stroke={theme.palette.secondary.main}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions + Recent Campaigns */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {quickActions.map((action, i) => (
                <Card
                  key={action.title + i}
                  sx={{ cursor: "pointer" }}
                  onClick={action.onClick}
                >
                  <CardActionArea sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          backgroundColor: alpha(action.color, 0.1),
                          borderRadius: "50%",
                          width: 40,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: action.color,
                        }}
                      >
                        {action.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2">
                          {action.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {action.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Recent Campaigns</Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate("/home/campaigns")}
              >
                View All
              </Button>
            </Box>
            {loadingRecent ? (
              <Skeleton variant="rounded" height={200} />
            ) : errorRecent ? (
              <Alert severity="error">{errorRecent}</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">Success Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentCampaigns.length > 0 ? (
                      recentCampaigns.map((camp) => {
                        const rate = camp.totalCalls
                          ? (camp.successfulCalls / camp.totalCalls) * 100
                          : 0;
                        return (
                          <TableRow
                            key={camp._id}
                            hover
                            sx={{ cursor: "pointer" }}
                            onClick={() =>
                              navigate(`/home/campaigns/${camp._id}`)
                            }
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {camp.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {format(
                                  new Date(camp.createdAt),
                                  "MMM dd, yyyy"
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={camp.status}
                                size="small"
                                color={
                                  camp.status === "completed"
                                    ? "success"
                                    : camp.status === "failed"
                                    ? "error"
                                    : "warning"
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              {camp.totalCalls.toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {rate.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No campaigns yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VoiceCallsDashboard;
